import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { trackEvent } from "@/utils/trackEvent";

import type { GraphCoordsSystem } from "@/components/graph/types";
import type { GraphNodeAttrs } from "@/components/tiptap/Graph";
import type { DocumentModel } from "@/models/DocumentModel";

export const ALIGNMENT_OFFSET = 0.005;

type Props = {
  doc: DocumentModel;
  graphAttrs: GraphNodeAttrs;
};

export const GraphAlignButtons = observer(function GraphAlignButtons({ doc, graphAttrs }: Props) {
  const t = useTranslations("sidebar.activeNode");
  const { tiptap } = doc.editor;
  const [xmin, , ymin] = graphAttrs.coordsSystem ?? [];

  if (!tiptap) {
    return null;
  }

  const handleGraphCoordsChange = (coordsSystem?: GraphCoordsSystem) => {
    tiptap.commands.updateAttributes("graph", { coordsSystem });
  };

  const positiveXActive = xmin === -ALIGNMENT_OFFSET && ymin === undefined;
  const positiveYActive = ymin === -ALIGNMENT_OFFSET && xmin === undefined;
  const positiveXYActive = xmin === -ALIGNMENT_OFFSET && ymin === -ALIGNMENT_OFFSET;

  const items = [
    {
      title: t("graph.alignment.positiveY"),
      icon: <Icon name="graph/GraphAlignY" />,
      active: positiveYActive,
      onClick: () => {
        if (positiveYActive) {
          handleGraphCoordsChange(undefined);
        } else {
          handleGraphCoordsChange([undefined, undefined, -ALIGNMENT_OFFSET, undefined]);
          trackEvent("graph_alignment_changed", { alignment: "positive_y" });
        }
      },
    },
    {
      title: t("graph.alignment.positiveX"),
      icon: <Icon name="graph/GraphAlignX" />,
      active: positiveXActive,
      onClick: () => {
        if (positiveXActive) {
          handleGraphCoordsChange(undefined);
        } else {
          handleGraphCoordsChange([-ALIGNMENT_OFFSET, undefined, undefined, undefined]);
          trackEvent("graph_alignment_changed", { alignment: "positive_x" });
        }
      },
    },
    {
      title: t("graph.alignment.positiveXY"),
      icon: <Icon name="graph/GraphAlignXY" />,
      active: positiveXYActive,
      onClick: () => {
        if (positiveXYActive) {
          handleGraphCoordsChange(undefined);
        } else {
          handleGraphCoordsChange([-ALIGNMENT_OFFSET, undefined, -ALIGNMENT_OFFSET, undefined]);
          trackEvent("graph_alignment_changed", { alignment: "positive_x_y" });
        }
      },
    },
  ];

  return (
    // `contents`: the radios stay items of the toolbar's own row
    <div role="radiogroup" aria-label={t("graph.alignment.title")} className="contents">
      {items.map((item) => (
        <Sidebar.Button
          key={item.title}
          icon={item.icon}
          aria-label={item.title}
          title={item.title}
          className="w-9.5"
          isActive={item.active}
          role="radio"
          aria-checked={item.active}
          onClick={item.onClick}
          inverted
        />
      ))}
    </div>
  );
});
